import { Margin, P, Button } from '@ovotech/element-v5';
import { useAnalytics, Event } from '@ovotech/ui-tools';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom-v5';
import { PageWithNavigation } from '@/src/components';
import {
  BoltonPlan,
  BoltonPlanStatus,
  EV,
  getPlanNameFromBolton,
  PlanDetails,
  useChargeAnytimeReinstateMutation,
  useChargeAnytimeProducts,
  useChargeAnytimeContext,
} from '@monovo/ev-anytime';
import { ROUTE_PLAN, ROUTE_ANYTIME_ELIGIBILITY } from '@/src/constants/routes';
import { canReinstateChargeAnytime } from '../../../Plan/components/Products/utils/chargeAnytimePlans';
import {
  getFormattedPlanTitle,
  isOemPlan,
} from '../ChargeAnytimeDisconnect/Confirmations';
import { useChargeAnytimeBoltonState } from '@/src/pages/AnytimeEligibilityV2/hooks/useChargeAnytimeBoltonState';
import { Media } from '@/src/components/Media';
import { breakpoints } from '@/src/utils/mediaQuery';
import { SkeletonCard } from '@/src/pages/Home/components/SkeletonCard';
import {
  SpacedBtnGroup,
  ButtonWrapper,
  StyledHeading1,
  FullWidthContentGrid,
  StyledCard,
  StyledHeading,
} from '../ChargeAnytimeDisconnect/Cancel.styled';

type ReinstateActionsProps = {
  reinstateAnytime: () => void;
  ctaText: string;
};

const ReinstateActions = ({
  reinstateAnytime,
  ctaText,
}: ReinstateActionsProps) => {
  const { dispatch: analyticsDispatch } = useAnalytics();
  const history = useHistory();

  const handleBack = () => {
    analyticsDispatch({
      type: 'click',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_REINSTATE_CONFIRM_BACK_CLICKED,
      properties: {
        pathname: '/anytime/reactivate',
        journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
        screen: EV.SCREENS.CONFIRM_REINSTATE,
      },
    });
    history.push(ROUTE_PLAN);
  };

  return (
    <SpacedBtnGroup>
      <Button data-testid="confirm-button" onClick={reinstateAnytime}>
        {ctaText}
      </Button>
      <Button
        onClick={handleBack}
        data-testid="reject-button"
        variant="outline"
        iconLeft="chevron-left"
      >
        Back
      </Button>
    </SpacedBtnGroup>
  );
};

const getPlanDetails = (
  boltonPlan: BoltonPlan,
  allPlans: PlanDetails[],
): PlanDetails | undefined => {
  const productType =
    boltonPlan.current?.productType || boltonPlan.future?.productType;
  return productType
    ? Object.values(allPlans).find(p => p.boltonProductType === productType)
    : undefined;
};

const ConfirmReinstate = ({
  reinstateAnytime,
  isLoading,
  boltonPlan,
  products,
  partnership,
}: {
  reinstateAnytime: () => void;
  isLoading: boolean;
  boltonPlan: BoltonPlan;
  products: PlanDetails[];
  partnership?: string | null;
}) => {
  if (isLoading) {
    return <SkeletonCard testId="skeleton-card" />;
  }

  const currentPlan = getPlanDetails(boltonPlan, products);
  const planTitle = currentPlan
    ? getFormattedPlanTitle(currentPlan, partnership, isOemPlan(currentPlan))
    : 'pay as you go';

  const pageTitle = 'Confirm reactivation';
  const cardHeading = `You're reactivating ${planTitle}`;
  const cardBody = 'Your plan will continue as normal.';

  return (
    <FullWidthContentGrid>
      <StyledHeading1>{pageTitle}</StyledHeading1>
      <StyledCard>
        <StyledHeading>{cardHeading}</StyledHeading>
        <div data-testid="confirm-reinstate-message">
          <P>{cardBody}</P>
        </div>
        <Media query={breakpoints.smallDown}>
          <ReinstateActions
            ctaText="Confirm reactivate bolton"
            reinstateAnytime={reinstateAnytime}
          />
        </Media>
      </StyledCard>
      <Media query={breakpoints.smallUp}>
        <ReinstateActions
          ctaText="Confirm reactivate bolton"
          reinstateAnytime={reinstateAnytime}
        />
      </Media>
    </FullWidthContentGrid>
  );
};

const ReinstateSuccess = ({
  boltonPlan,
  products,
  partnership,
}: {
  boltonPlan: BoltonPlan;
  products: PlanDetails[];
  partnership?: string | null;
}) => {
  const { dispatch: analyticsDispatch } = useAnalytics();
  const history = useHistory();

  useEffect(() => {
    analyticsDispatch({
      type: 'view',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_REINSTATE_SUCCESS_VIEWED,
      properties: {
        pathname: '/anytime/reactivate',
        journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
        screen: EV.SCREENS.SUCCESS_REINSTATE,
      },
    });
  }, [analyticsDispatch]);

  const goToPlanPage = () => {
    analyticsDispatch({
      type: 'click',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_REINSTATE_SUCCESS_BACK_CLICKED,
      properties: {
        pathname: '/anytime/reactivate',
        journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
        screen: EV.SCREENS.SUCCESS_REINSTATE,
      },
    });
    history.push(ROUTE_PLAN);
  };

  const currentPlan = getPlanDetails(boltonPlan, products);
  const planTitle = currentPlan
    ? getFormattedPlanTitle(currentPlan, partnership, isOemPlan(currentPlan))
    : 'pay as you go';

  const pageTitle = 'Reactivation success';
  const cardHeading = `You're back on ${planTitle}`;
  const cardBody = `We've reactivated ${planTitle}. Your plan will continue as normal.`;

  return (
    <FullWidthContentGrid>
      <StyledHeading1>{pageTitle}</StyledHeading1>
      <StyledCard>
        <StyledHeading>{cardHeading}</StyledHeading>
        <P>{cardBody}</P>
        <Media query={breakpoints.smallDown}>
          <ButtonWrapper>
            <Button onClick={goToPlanPage} data-testid="close-button">
              Back to my account
            </Button>
          </ButtonWrapper>
        </Media>
      </StyledCard>
      <Media query={breakpoints.smallUp}>
        <ButtonWrapper>
          <Button onClick={goToPlanPage} data-testid="close-button">
            Back to my account
          </Button>
        </ButtonWrapper>
      </Media>
    </FullWidthContentGrid>
  );
};

const ReinstateError = () => {
  const { dispatch: analyticsDispatch } = useAnalytics();
  const history = useHistory();

  useEffect(() => {
    analyticsDispatch({
      type: 'view',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_REINSTATE_FAILED_VIEWED,
      properties: {
        pathname: '/anytime/reactivate',
        journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
        screen: EV.SCREENS.FAILED_REINSTATE,
      },
    });
  }, [analyticsDispatch]);

  const handleBack = () => {
    analyticsDispatch({
      type: 'click',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_REINSTATE_FAILED_BACK_CLICKED,
      properties: {
        pathname: '/anytime/reactivate',
        journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
        screen: EV.SCREENS.FAILED_REINSTATE,
      },
    });
    history.push(ROUTE_PLAN);
  };

  return (
    <FullWidthContentGrid>
      <StyledHeading1>Something went wrong</StyledHeading1>
      <StyledCard>
        <P>
          We couldn't reinstate your Charge Anytime plan. Go back and try again.
        </P>
        <Margin top={8}>
          <Button
            data-testid="back-button"
            onClick={handleBack}
            variant="outline"
            iconLeft="chevron-left"
          >
            Back
          </Button>
        </Margin>
      </StyledCard>
    </FullWidthContentGrid>
  );
};

export const ChargeAnytimeReactivate = () => {
  const { dispatch: analyticsDispatch } = useAnalytics();
  const history = useHistory();

  const { partnership: contextPartnership } = useChargeAnytimeContext();
  const { isFetching, boltonPlan, signupIntegrationType } =
    useChargeAnytimeBoltonState();
  const rawPartnership = contextPartnership || signupIntegrationType;
  const partnership =
    rawPartnership &&
    rawPartnership.charAt(0).toUpperCase() +
      rawPartnership.slice(1).toLowerCase();

  const { data: products, isLoading: isLoadingProducts } =
    useChargeAnytimeProducts('all');

  const {
    isLoading: isMutationLoading,
    mutate: reinstateChargeAnytime,
    isSuccess,
    isError,
  } = useChargeAnytimeReinstateMutation();

  const [finalBoltonPlan, setFinalBoltonPlan] = useState<BoltonPlan | null>(
    null,
  );

  const canReinstate = canReinstateChargeAnytime(boltonPlan!);

  useEffect(() => {
    // Only redirect if the query is fully loaded AND the mutation hasn't succeeded.
    // This prevents redirecting to the Plan page immediately when the reactivation succeeds.
    if (!isFetching && !isLoadingProducts && !isSuccess) {
      if (
        !boltonPlan ||
        boltonPlan.status === BoltonPlanStatus.NoChargeAnytime
      ) {
        // CASE 1: The user has no Charge Anytime bolton (or it has fully expired and been removed).
        // Redirect them to the Introduction page so they can check eligibility and sign up fresh.
        history.replace(ROUTE_ANYTIME_ELIGIBILITY.INTRODUCTION);
      } else if (!canReinstate) {
        // CASE 2: The user has a plan, but they are NOT in a 'Cancelled' (pending-cancellation) state.
        // E.g., they are already 'Active', 'Initialized', or 'Switching'.
        // Redirect them to the Plan page to view their current active plan details.
        history.replace(ROUTE_PLAN);
      }
    }
  }, [
    boltonPlan,
    canReinstate,
    isFetching,
    isLoadingProducts,
    isSuccess,
    history,
  ]);

  useEffect(() => {
    if (boltonPlan && products && canReinstate) {
      analyticsDispatch({
        type: 'view',
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_REINSTATE_CONFIRM_VIEWED,
        properties: {
          pathname: '/anytime/reactivate',
          journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
          screen: EV.SCREENS.CONFIRM_REINSTATE,
        },
      });
    }
    // analyticsDispatch should not be a dependency here, as it would cause unnecessary re-renders and the analytics dispatch function is guaranteed to be stable by the useAnalytics hook implementation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boltonPlan, products, canReinstate]);

  const handleReinstate = () => {
    if (boltonPlan && products) {
      analyticsDispatch({
        type: 'submitted' as Event['type'],
        name: EV.ELIGIBILITY.CHARGE_ANYTIME_REINSTATE_CONFIRM_NEXT_CLICKED,
        properties: {
          reinstatedAddOnName: getPlanNameFromBolton(
            boltonPlan.current,
            products,
          ),
          pathname: '/anytime/reactivate',
          journeyName: EV.ELIGIBILITY.CHARGE_ANYTIME_JOURNEY_NAME,
          screen: EV.SCREENS.CONFIRM_REINSTATE,
        },
      });
    }
    reinstateChargeAnytime();
  };

  useEffect(() => {
    // If the mutation was successful AND we haven't already saved the plan
    if (isSuccess && !finalBoltonPlan && boltonPlan) {
      // Save the current plan details into our local state
      setFinalBoltonPlan(boltonPlan);
    }
  }, [isSuccess, boltonPlan, finalBoltonPlan]);

  // Handle page-level loading ONLY on first-load (when neither plan is loaded yet).
  // This prevents the refetch-induced loading spinner from wiping out the page when mutating.
  if (isFetching || isLoadingProducts) {
    if (!boltonPlan && !finalBoltonPlan) {
      return <SkeletonCard testId="skeleton-card" />;
    }
  }

  // Handle redirect fallback skeleton
  if (!isSuccess && (!boltonPlan || !canReinstate)) {
    return <SkeletonCard testId="skeleton-card" />;
  }

  const renderContent = () => {
    if (!boltonPlan && !finalBoltonPlan) {
      return null;
    }

    if (isSuccess) {
      return finalBoltonPlan ? (
        <ReinstateSuccess
          boltonPlan={finalBoltonPlan}
          products={products!}
          partnership={partnership}
        />
      ) : (
        <SkeletonCard testId="skeleton-card" />
      );
    }

    if (isError) {
      return <ReinstateError />;
    }

    return (
      <ConfirmReinstate
        reinstateAnytime={handleReinstate}
        isLoading={isMutationLoading}
        boltonPlan={boltonPlan!}
        products={products!}
        partnership={partnership}
      />
    );
  };

  return (
    <PageWithNavigation title="Charge Anytime">
      <Margin top={10}>{renderContent()}</Margin>
    </PageWithNavigation>
  );
};
